import { Search, PenTool, Wrench, Headphones } from "lucide-react";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const steps = [
  {
    icon: Search,
    number: "01",
    title: "Diagnóstico",
    description: "Entendemos necesidad, operación y puntos críticos"
  },
  {
    icon: PenTool,
    number: "02",
    title: "Diseño de ingeniería",
    description: "Propuesta optimizada (recursos + rendimiento)"
  },
  {
    icon: Wrench,
    number: "03",
    title: "Instalación y pruebas",
    description: "Configuración completa y verificación"
  },
  {
    icon: Headphones,
    number: "04",
    title: "Mantenimiento y soporte",
    description: "Continuidad real, sin \"desaparecer\" después"
  }
];

const NewProcess = () => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section 
      id="proceso" 
      ref={ref as React.RefObject<HTMLElement>}
      className="py-14 md:py-20 bg-background scroll-mt-20"
    >
      <div className="container px-4 sm:px-6">
        <div className={cn(
          "text-center mb-10 sm:mb-16 transition-all duration-700",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        )}>
          <span className="inline-block text-primary font-medium mb-3 uppercase tracking-wide text-sm">
            Proceso
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-4">
            Cómo trabajamos
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
            Un proceso claro y ordenado para que sepas exactamente qué esperar.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          {/* Desktop: horizontal timeline */}
          <div className="hidden md:block relative">
            {/* Line connector */}
            <div className={cn(
              "absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-secondary to-primary transition-all duration-1000",
              isVisible ? "scale-x-100" : "scale-x-0"
            )} style={{ transformOrigin: "left" }} />
            
            <div className="grid grid-cols-4 gap-6">
              {steps.map((step, index) => (
                <div 
                  key={index} 
                  className={cn(
                    "relative text-center transition-all duration-500",
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                  )}
                  style={{ transitionDelay: isVisible ? `${200 + index * 150}ms` : "0ms" }}
                >
                  {/* Icon circle */}
                  <div className="relative z-10 w-24 h-24 mx-auto mb-6 rounded-full bg-card border-4 border-primary/20 flex items-center justify-center group hover:border-primary/50 transition-colors">
                    <step.icon className="w-10 h-10 text-primary" />
                    <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">
                      {step.number}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile: vertical list */}
          <div className="md:hidden space-y-6">
            {steps.map((step, index) => (
              <div 
                key={index} 
                className={cn(
                  "flex gap-4 items-start transition-all duration-500",
                  isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                )}
                style={{ transitionDelay: isVisible ? `${100 + index * 100}ms` : "0ms" }}
              >
                <div className="relative flex-shrink-0">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                    <step.icon className="w-6 h-6 text-primary" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                    {step.number}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewProcess;
